import { useEffect, useState, type CSSProperties } from 'react';
import { theme, FONT_MONO } from '../theme';
import { api, type AppItem, type GuestLinkSummary } from '../api';
import { CopyBtn } from './primitives';

const DURATIONS = [
  { label: '1 hour',  ms: 1000 * 60 * 60 },
  { label: '24 hours', ms: 1000 * 60 * 60 * 24 },
  { label: '7 days',  ms: 1000 * 60 * 60 * 24 * 7 },
  { label: '30 days', ms: 1000 * 60 * 60 * 24 * 30 },
];

function fmtDate(ms: number): string {
  return new Date(ms).toLocaleString();
}

function fmtRemaining(ms: number): string {
  const delta = ms - Date.now();
  if (delta <= 0) return 'expired';
  const h = Math.floor(delta / (1000 * 60 * 60));
  if (h < 24) return `${h}h left`;
  const d = Math.floor(h / 24);
  return `${d}d left`;
}

export function GuestLinkPanel({
  apps,
  onClose,
}: {
  apps: AppItem[];
  onClose: () => void;
}) {
  const [links, setLinks] = useState<GuestLinkSummary[]>([]);
  const [pickedApps, setPickedApps] = useState<Set<string>>(new Set());
  const [duration, setDuration] = useState(DURATIONS[1].ms);
  const [maxUses, setMaxUses] = useState<string>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    api.guestLinks().then((r) => setLinks(r.links)).catch(() => {});
  };

  useEffect(refresh, []);

  const toggleApp = (id: string) => {
    setPickedApps((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const create = async () => {
    setError(null);
    if (pickedApps.size === 0) {
      setError('Pick at least one app.');
      return;
    }
    setBusy(true);
    try {
      const max = maxUses.trim() ? Number(maxUses) : null;
      if (max != null && (!Number.isFinite(max) || max <= 0)) {
        setError('Max uses must be a positive number.');
        setBusy(false);
        return;
      }
      await api.createGuestLink({
        appIds: Array.from(pickedApps),
        expiresAt: Date.now() + duration,
        maxUses: max,
        note: note.trim() || null,
      });
      setPickedApps(new Set());
      setMaxUses('');
      setNote('');
      refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    await api.revokeGuestLink(id).catch(() => {});
    refresh();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'grid', placeItems: 'center', zIndex: 100, padding: 24,
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 720, maxHeight: '90vh', overflow: 'auto',
          background: theme.surface, border: `1px solid ${theme.line}`,
          color: theme.text, fontFamily: 'inherit', borderRadius: 16,
        }}
      >
        <div style={{
          padding: '18px 24px', borderBottom: `1px solid ${theme.line}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Guest links</div>
          <button onClick={onClose} style={iconBtn}
            onMouseEnter={(e) => { e.currentTarget.style.color = theme.text; e.currentTarget.style.borderColor = theme.line2; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = theme.dim; e.currentTarget.style.borderColor = theme.line; }}
          >Close</button>
        </div>

        <div style={{ padding: 24, borderBottom: `1px solid ${theme.line}` }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: theme.text }}>
            Create new link
          </div>

          <FieldLabel>Apps</FieldLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 }}>
            {apps.map((a) => {
              const active = pickedApps.has(a.id);
              return (
                <button
                  key={a.id}
                  onClick={() => toggleApp(a.id)}
                  style={{
                    padding: '6px 12px', borderRadius: 8,
                    background: active ? theme.accent : 'transparent',
                    color: active ? theme.bg : theme.dim,
                    border: `1px solid ${active ? theme.accent : theme.line}`,
                    fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                    transition: 'all 120ms',
                  }}
                >{a.name}</button>
              );
            })}
            {apps.length === 0 && (
              <span style={{ color: theme.dim, fontSize: 12 }}>No apps configured.</span>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <FieldLabel>Expires in</FieldLabel>
              <select
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                style={inputStyle}
              >
                {DURATIONS.map((d) => (
                  <option key={d.ms} value={d.ms}>{d.label}</option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel>Max uses (optional)</FieldLabel>
              <input
                type="number" min="1"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                placeholder="unlimited"
                style={inputStyle}
              />
            </div>
          </div>

          <FieldLabel>Note (optional)</FieldLabel>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="who is this for?"
            style={{ ...inputStyle, marginBottom: 14 }}
          />

          {error && (
            <div style={{ color: theme.err, fontSize: 12, marginBottom: 12 }}>{error}</div>
          )}

          <button
            onClick={create}
            disabled={busy}
            style={{
              padding: '10px 18px', background: theme.accent, color: theme.bg,
              border: 'none', borderRadius: 10,
              cursor: busy ? 'wait' : 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            }}
          >
            {busy ? 'Creating…' : 'Create link'}
          </button>
        </div>

        <div style={{ padding: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: theme.text }}>
            Existing links
          </div>
          {links.length === 0 && (
            <div style={{ color: theme.dim, fontSize: 12 }}>None yet.</div>
          )}
          {links.map((l) => (
            <div key={l.id} style={{
              border: `1px solid ${theme.line}`, borderRadius: 12, padding: 14, marginBottom: 10,
              background: theme.bg,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{
                  fontSize: 10, padding: '3px 8px', borderRadius: 6,
                  background: l.active ? theme.ok : theme.veryDim,
                  color: theme.bg, letterSpacing: '0.1em', textTransform: 'uppercase',
                  fontWeight: 600,
                }}>{l.active ? 'active' : (l.revokedAt ? 'revoked' : 'expired')}</span>
                <span style={{ fontSize: 12, color: theme.dim }}>
                  {l.appIds.length} app{l.appIds.length === 1 ? '' : 's'} ·{' '}
                  {l.active ? fmtRemaining(l.expiresAt) : fmtDate(l.expiresAt)}
                </span>
                {l.note && <span style={{ fontSize: 12, color: theme.dim }}>· {l.note}</span>}
                <span style={{ marginLeft: 'auto', fontSize: 11, color: theme.veryDim }}>
                  {l.usedCount}{l.maxUses ? ` / ${l.maxUses}` : ''} uses
                </span>
              </div>
              <div style={{
                display: 'flex', gap: 8, alignItems: 'center',
                fontFamily: FONT_MONO, fontSize: 11, color: theme.dim,
                wordBreak: 'break-all',
              }}>
                <span>{l.url}</span>
                <CopyBtn value={l.url} />
                {l.active && (
                  <button
                    onClick={() => revoke(l.id)}
                    style={{
                      marginLeft: 'auto', background: 'transparent', cursor: 'pointer',
                      border: `1px solid ${theme.line}`, color: theme.err,
                      padding: '4px 10px', fontSize: 11, fontFamily: 'inherit',
                      borderRadius: 6,
                    }}
                  >Revoke</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 11, color: theme.dim, marginBottom: 6, fontWeight: 500,
    }}>{children}</div>
  );
}

const inputStyle: CSSProperties = {
  width: '100%', padding: '10px 12px', background: theme.bg,
  border: `1px solid ${theme.line}`, borderRadius: 10, color: theme.text,
  fontFamily: 'inherit', fontSize: 13, outline: 'none',
};

const iconBtn: CSSProperties = {
  background: 'transparent', border: `1px solid ${theme.line}`,
  color: theme.dim, padding: '6px 12px', borderRadius: 8,
  fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
  transition: 'color 120ms, border-color 120ms',
};
